import { useEffect, useState } from 'react';
import { TIER_CATALOG, annualSavingGbp, type BillingCycle } from '../lib/billing';
import {
  initIap,
  getOfferings,
  purchasePackage,
  restorePurchases,
  hasProEntitlement,
  productToTier,
} from '../lib/iap';
import { nativePlatform } from '../lib/nativeShell';
import type {
  PurchasesOffering,
  PurchasesPackage,
} from '@revenuecat/purchases-capacitor';

/**
 * In-App Purchase plan picker for the iOS / Android shells.
 *
 * Shared by Paywall (new customers) and Plan & billing (existing
 * customers changing plan). Apple Guideline 3.1.1: inside the native
 * app, the ONLY way to buy or change a subscription is Apple IAP — no
 * links, buttons or URLs pointing at our website or Stripe.
 *
 * Renders: Monthly/Annual toggle → one card per RevenueCat package →
 * Restore purchases → "Manage in App Store" (Apple's own subscription
 * page — the one external link Apple explicitly allows).
 */
export function IapPlans({
  userId,
  currentTier,
  onPurchased,
}: {
  userId: string | undefined;
  /** Highlights the tier the org is already on (Plan page). */
  currentTier?: string | null;
  /** Called after a successful purchase or restore that grants "pro". */
  onPurchased: () => void;
}) {
  const isAndroid = nativePlatform() === 'android';
  const storeName = isAndroid ? 'Google Play' : 'the App Store';
  const accountName = isAndroid ? 'Google account' : 'Apple ID';
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [offering, setOffering] = useState<PurchasesOffering | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await initIap(userId);
        const off = await getOfferings();
        if (cancelled) return;
        if (!off) setError('Subscription plans are temporarily unavailable. Please try again in a minute.');
        setOffering(off);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  async function onBuy(pkg: PurchasesPackage) {
    setBusy(pkg.identifier);
    setError(null);
    try {
      const ci = await purchasePackage(pkg);
      if (hasProEntitlement(ci)) {
        onPurchased();
      } else {
        setError('Purchase completed but subscription not yet active — please try again in a minute.');
      }
    } catch (e) {
      const err = e as { userCancelled?: boolean; message?: string };
      if (!err?.userCancelled) setError(err?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onRestore() {
    setBusy('restore');
    setError(null);
    try {
      const ci = await restorePurchases();
      if (hasProEntitlement(ci)) onPurchased();
      else setError(`No previous purchases found on this ${accountName}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      {loading && (
        <div className="card" style={{ marginBottom: 10 }}>
          <p className="muted" style={{ margin: 0 }}>Loading subscription plans…</p>
        </div>
      )}

      {!loading && offering && (
        <>
          <div role="tablist" aria-label="Billing cycle" className="rams-trade-chips" style={{ marginBottom: 12 }}>
            <button
              type="button" role="tab" aria-selected={cycle === 'monthly'}
              className={`rams-trade-chip ${cycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setCycle('monthly')}
            >
              Monthly
            </button>
            <button
              type="button" role="tab" aria-selected={cycle === 'annual'}
              className={`rams-trade-chip ${cycle === 'annual' ? 'active' : ''}`}
              onClick={() => setCycle('annual')}
            >
              Annual <span style={{ opacity: 0.85, fontWeight: 500 }}>· 2 months free</span>
            </button>
          </div>

          {offering.availablePackages
            .map((pkg) => ({ pkg, mapping: productToTier(pkg.product.identifier) }))
            .filter(({ mapping }) => mapping && mapping.cycle === cycle)
            .sort((a, b) => {
              const order = { tier1: 0, tier2: 1, tier3: 2 } as const;
              return order[a.mapping!.tier] - order[b.mapping!.tier];
            })
            .map(({ pkg, mapping }) => {
              const tier = mapping!.tier;
              const cat = TIER_CATALOG[tier];
              const isCurrent = currentTier === tier;
              // Free-trial detection. iOS exposes the App Store introductory
              // offer as introPrice (price 0 = free trial); Android exposes the
              // Play free phase on the default subscription option. Only show the
              // trial line when the store actually offers it to this user, so we
              // never promise a trial Apple/Google won't honour (Guideline 3.1.2).
              const product = pkg.product as typeof pkg.product & {
                defaultOption?: { freePhase?: unknown } | null;
              };
              const hasFreeTrial =
                (product.introPrice != null && product.introPrice.price === 0) ||
                product.defaultOption?.freePhase != null;
              const priceStr = pkg.product.priceString
                ?? `£${cycle === 'annual' ? cat.yearlyGbp : cat.monthlyGbp}`;
              return (
                <div key={pkg.identifier} className="card" style={{
                  marginBottom: 10,
                  borderColor: isCurrent ? 'var(--success, #2F6B47)' : undefined,
                  borderWidth: isCurrent ? 2 : 1,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>
                      {cat.label}
                      {isCurrent && <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--success, #2F6B47)' }}>· current plan</span>}
                    </p>
                    <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                      {priceStr}
                      <span style={{ fontSize: 13, fontWeight: 400 }}>{cycle === 'annual' ? '/year' : '/month'}</span>
                    </p>
                  </div>
                  <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
                    {cat.phones} · unlimited sites, sub-contractors, sign-ins
                  </p>
                  {hasFreeTrial && (
                    <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600 }}>
                      30 days free, then {priceStr}{cycle === 'annual' ? '/year' : '/month'}
                    </p>
                  )}
                  {cycle === 'annual' && (
                    <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--success, #2F6B47)', fontWeight: 600 }}>
                      Save £{annualSavingGbp(tier)} vs paying monthly
                    </p>
                  )}
                  <button
                    type="button" className="btn btn-primary" style={{ width: '100%', fontSize: 14 }}
                    disabled={busy === pkg.identifier}
                    onClick={() => onBuy(pkg)}
                  >
                    {busy === pkg.identifier
                      ? (isAndroid ? 'Opening Google Play…' : 'Opening Apple…')
                      : hasFreeTrial
                        ? `Start 30-day free trial — ${cat.label}${cycle === 'annual' ? ' annual' : ''}`
                        : `Subscribe — ${cat.label}${cycle === 'annual' ? ' annual' : ''}`}
                  </button>
                </div>
              );
            })}
        </>
      )}

      {/* Restore purchases — required by Apple for every subscription app. */}
      <div className="card" style={{ marginTop: 8, background: 'var(--bg-soft, #FAF7F2)' }}>
        <p style={{ margin: 0, fontWeight: 600 }}>Already subscribed?</p>
        <p className="muted" style={{ margin: '4px 0 8px', fontSize: 13 }}>
          If you've bought a plan on this {accountName} before, restore it here.
        </p>
        <button
          type="button" className="btn btn-ghost" style={{ width: '100%', fontSize: 14 }}
          disabled={busy === 'restore'}
          onClick={onRestore}
        >
          {busy === 'restore' ? 'Restoring…' : 'Restore purchases'}
        </button>
        <button
          type="button" className="btn btn-ghost" style={{ width: '100%', fontSize: 14, marginTop: 8 }}
          onClick={() => {
            // Apple's own subscription-management page. This is the one
            // external link Apple explicitly permits (and recommends).
            window.open(isAndroid ? 'https://play.google.com/store/account/subscriptions?package=com.safeandsorted.app' : 'https://apps.apple.com/account/subscriptions', '_blank');
          }}
        >
          Manage or cancel in {storeName}
        </button>
      </div>

      <p className="muted" style={{ fontSize: 12, marginTop: 18, lineHeight: 1.5 }}>
        New subscribers get a 30-day free trial. Payment is charged to your {accountName}
        when the trial ends, unless you cancel at least 24 hours before it ends. Your
        subscription then auto-renews unless you cancel at least 24 hours before the end of the
        current period. Manage or cancel any time in
        {isAndroid ? 'Google Play → Payments & subscriptions → Subscriptions.' : 'Settings → your Apple ID → Subscriptions.'}
      </p>
      <p className="muted" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
        By subscribing you agree to our{' '}
        <a href="https://safeandsorted.com/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use (EULA)</a>
        {' '}and{' '}
        <a href="https://safeandsorted.com/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </p>
    </>
  );
}
